import { useEffect, useRef, useState } from 'react';
import { formatBrowserModelError, streamBrowserInterpretation } from '../ai/browserModel';
import type { LocalVisionSummary } from '../ai/localVision';
import { buildSafeFollowUpFallback, buildSafeInterpretationFallback, coerceInterpretation } from '../ai/answer';
import { normalizeAiBaseUrl, streamInterpretation } from '../ai/client';
import { chooseAutomaticInterpretation, detectInterpretationEnvironment } from '../ai/deviceRouting';
import { clearAiDeviceConfig, loadAiDeviceConfig, saveAiDeviceConfig } from '../ai/deviceConfig';
import { isBrowserModelPreset, isOfflinePreset, fullDestination, normalizedTarget, providerPresets, requiresApiKey, type ProviderPreset } from '../ai/providers';
import { buildRuleInterpretation } from '../ai/ruleInterpreter';
import type { ChartModuleId, ChartResult } from '../domain/types';
import { imageKnowledgeContextFor, knowledgeContextFor } from '../knowledge/catalog';
import { AiConversation, type ConversationTurn } from './AiConversation';
import { Icon } from './Icon';
import { ImageReadingInput, type ImageReadingDraft } from './ImageReadingInput';
import { InterpretationModeCard } from './InterpretationModeCard';

type ConfigField = 'baseUrl' | 'model' | 'apiKey';

const DEFAULT_QUESTION = '这次盘面主要说明什么？直接告诉我。';
const QUESTION_PRESETS = [
  { label: '看重点', value: '这次盘面最主要说明什么？直接告诉我。' },
  { label: '看变化', value: '这件事接下来可能有什么变化？' },
  { label: '看下一步', value: '我现在最该做什么？' }
];
const PROVIDER_LABELS: Record<ProviderPreset, string> = {
  auto: '自动适配',
  rules: '免费规则解读',
  browser: '轻量本地模型',
  browserQuality: '优质本地模型',
  deepseek: 'DeepSeek',
  openai: 'OpenAI',
  local: '本机 Ollama',
  custom: '自定义兼容接口'
};
const MAX_VISIBLE_ANSWER = 200_000;

export function AiPanel({ result, moduleId }: { result: ChartResult | null; moduleId: ChartModuleId }) {
  const [environment] = useState(() => detectInterpretationEnvironment());
  const [preset, setPreset] = useState<ProviderPreset>('auto');
  const [baseUrl, setBaseUrl] = useState(providerPresets.auto.baseUrl);
  const [model, setModel] = useState(providerPresets.auto.model);
  const [apiKey, setApiKey] = useState('');
  const [apiKeyTarget, setApiKeyTarget] = useState('');
  const [deviceConfigReady, setDeviceConfigReady] = useState(false);
  const [rememberOnDevice, setRememberOnDevice] = useState(true);
  const [hasSavedConfig, setHasSavedConfig] = useState(false);
  const [deviceConfigError, setDeviceConfigError] = useState('');
  const [clearingDeviceConfig, setClearingDeviceConfig] = useState(false);
  const [question, setQuestion] = useState(result?.question || DEFAULT_QUESTION);
  const [questionResult, setQuestionResult] = useState<ChartResult | null>(result);
  const [answer, setAnswer] = useState('');
  const [answerResult, setAnswerResult] = useState<ChartResult | null>(null);
  const [conversation, setConversation] = useState<ConversationTurn[]>([]);
  const [conversationKey, setConversationKey] = useState(result?.generatedAt || 'empty');
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [errorResult, setErrorResult] = useState<ChartResult | null>(null);
  const [errorField, setErrorField] = useState<ConfigField | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingResult, setLoadingResult] = useState<ChartResult | null>(null);
  const [loadingModuleId, setLoadingModuleId] = useState<ChartModuleId | null>(null);
  const [imageAttachment, setImageAttachment] = useState<ImageReadingDraft | null>(null);
  const [imageAnalysis, setImageAnalysis] = useState<LocalVisionSummary | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [imagePanelOpen, setImagePanelOpen] = useState(false);
  const [modelSettingsVisible, setModelSettingsVisible] = useState(true);
  const controllerRef = useRef<AbortController | null>(null);
  const requestContextRef = useRef<{ result: ChartResult; moduleId: ChartModuleId } | null>(null);
  const answeredQuestionRef = useRef('');
  const answerBufferRef = useRef('');
  const answerCommittedRef = useRef(false);
  const baseUrlRef = useRef<HTMLInputElement>(null);
  const modelRef = useRef<HTMLInputElement>(null);
  const apiKeyRef = useRef<HTMLInputElement>(null);
  const configTouchedRef = useRef(false);
  const visibleQuestion = questionResult === result ? question : result?.question || DEFAULT_QUESTION;
  const visibleAnswer = answerResult === result ? answer : '';
  const visibleError = errorResult === result ? error : '';
  const visibleErrorField = errorResult === result ? errorField : null;
  const resultKey = result?.generatedAt || 'empty';
  const visibleConversation = conversationKey === resultKey ? conversation : [];
  const automaticInterpretation = chooseAutomaticInterpretation(environment);
  const effectivePreset: ProviderPreset = preset === 'auto'
    ? automaticInterpretation === 'rules' ? 'rules' : 'browser'
    : preset;
  const effectiveModel = preset === 'auto' ? providerPresets[effectivePreset].model : model;
  const apiKeyRequired = requiresApiKey(preset, baseUrl);
  const currentLoading = loading
    && loadingResult === result
    && loadingModuleId === moduleId;
  const activeImageAttachment = imageAttachment;
  const hasPreviousAnswer = Boolean(visibleAnswer.trim() || visibleConversation.length > 0);

  useEffect(() => {
    let cancelled = false;
    void loadAiDeviceConfig()
      .then((saved) => {
        if (cancelled || !saved || configTouchedRef.current) return;
        setPreset(saved.preset);
        setBaseUrl(saved.baseUrl);
        setModel(saved.model);
        setApiKey(saved.apiKey);
        setApiKeyTarget(saved.apiKeyTarget);
        setSettingsOpen(!isOfflinePreset(saved.preset));
        setHasSavedConfig(true);
      })
      .catch(() => {
        // 不支持 IndexedDB 或 Web Crypto 时仍可使用，只是不做跨刷新保存。
      })
      .finally(() => {
        if (!cancelled) setDeviceConfigReady(true);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!deviceConfigReady || !configTouchedRef.current || !rememberOnDevice) return;
    const timeoutId = window.setTimeout(() => {
      void saveAiDeviceConfig({
        version: 1,
        preset,
        baseUrl,
        model,
        apiKey: isOfflinePreset(preset) ? '' : apiKey,
        apiKeyTarget: isOfflinePreset(preset) ? '' : apiKeyTarget
      }).then(() => {
        setHasSavedConfig(true);
        setDeviceConfigError('');
      }).catch(() => {
        setDeviceConfigError('当前浏览器没能保存设置；本次仍可正常使用，刷新后需要重新填写。');
      });
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [apiKey, apiKeyTarget, baseUrl, deviceConfigReady, model, preset, rememberOnDevice]);

  useEffect(() => {
    answerBufferRef.current = '';
    answerCommittedRef.current = false;

    return () => {
      const activeController = controllerRef.current;
      controllerRef.current = null;
      requestContextRef.current = null;
      activeController?.abort();
      answerBufferRef.current = '';
      answerCommittedRef.current = false;
    };
  }, [moduleId, result]);

  function handlePreset(value: ProviderPreset) {
    controllerRef.current?.abort();
    configTouchedRef.current = true;
    setPreset(value);
    setBaseUrl(providerPresets[value].baseUrl);
    setModel(providerPresets[value].model);
    setApiKey('');
    setApiKeyTarget('');
    setError('');
    setStatus('');
    setErrorField(null);
    setSettingsOpen(!isOfflinePreset(value));
  }

  function handleBaseUrl(value: string) {
    configTouchedRef.current = true;
    setBaseUrl(value);
    setApiKey('');
    setApiKeyTarget('');
    setError('');
    setStatus('');
    setErrorField(null);
  }

  function handleApiKey(value: string) {
    configTouchedRef.current = true;
    setApiKey(value);
    setApiKeyTarget(value ? normalizedTarget(baseUrl) : '');
    if (errorField === 'apiKey') {
      setError('');
      setErrorField(null);
    }
  }

  function handleRememberOnDevice(value: boolean) {
    configTouchedRef.current = true;
    setRememberOnDevice(value);
    setDeviceConfigError('');
    if (!value) {
      setClearingDeviceConfig(true);
      void clearAiDeviceConfig()
        .then(() => setHasSavedConfig(false))
        .catch(() => {
          setRememberOnDevice(true);
          setHasSavedConfig(true);
          setDeviceConfigError('本机保存尚未清除，请关闭其他易鉴页面后重试。');
        })
        .finally(() => setClearingDeviceConfig(false));
    }
  }

  async function clearDeviceCredentials() {
    controllerRef.current?.abort();
    configTouchedRef.current = true;
    setRememberOnDevice(false);
    setApiKey('');
    setApiKeyTarget('');
    setDeviceConfigError('');
    setClearingDeviceConfig(true);
    try {
      await clearAiDeviceConfig();
      setHasSavedConfig(false);
    } catch {
      setHasSavedConfig(true);
      setDeviceConfigError('本机保存尚未清除，请关闭其他易鉴页面后重试。');
    } finally {
      setClearingDeviceConfig(false);
    }
  }

  async function interpret(questionOverride?: string) {
    if (controllerRef.current) return;
    const effectiveQuestion = questionOverride?.trim() || visibleQuestion.trim() || DEFAULT_QUESTION;
    const previousTurn = visibleConversation.at(-1);
    const previousAnswer = visibleAnswer.trim() || previousTurn?.answer.trim() || '';
    const previousQuestion = answeredQuestionRef.current || previousTurn?.question || visibleQuestion;
    const hasPreviousAnswerForRequest = Boolean(previousAnswer);
    setError('');
    setErrorResult(result);
    setErrorField(null);
    setAnswer('');
    setAnswerResult(result);
    setQuestionResult(result);
    setQuestion(effectiveQuestion);
    setFollowUpQuestion('');
    answerBufferRef.current = '';
    answerCommittedRef.current = false;
    if (!result) {
      setError('请先完成一次排盘，再调用 AI 解读。');
      return;
    }
    if (activeImageAttachment && !imageAnalysis) {
      setError('请先点击“本机分析图片”。原图不会上传，浏览器本地模型只会使用本机生成的结构摘要。');
      return;
    }
    if (activeImageAttachment && !isOfflinePreset(effectivePreset)) {
      setError('图片研究目前只支持浏览器本地免费模型，远程接口不会接收图片或图像摘要。');
      return;
    }
    if (!isOfflinePreset(effectivePreset)) {
      try {
        normalizeAiBaseUrl(baseUrl);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'AI 服务地址格式无效。');
        setErrorField('baseUrl');
        baseUrlRef.current?.focus();
        return;
      }
    }
    if (effectivePreset !== 'rules' && !effectiveModel.trim()) {
      setError('请输入模型名称。');
      setErrorField('model');
      modelRef.current?.focus();
      return;
    }
    if (apiKeyRequired && !apiKey.trim()) {
      setError('请输入 API Key。');
      setErrorField('apiKey');
      apiKeyRef.current?.focus();
      return;
    }
    if (!isOfflinePreset(effectivePreset) && apiKey.trim() && apiKeyTarget !== normalizedTarget(baseUrl)) {
      setError('API 地址已经改变。为防止密钥误发，请重新输入对应服务的 API Key。');
      setErrorField('apiKey');
      setApiKey('');
      setApiKeyTarget('');
      apiKeyRef.current?.focus();
      return;
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    requestContextRef.current = { result, moduleId };
    setLoadingResult(result);
    setLoadingModuleId(moduleId);
    setLoading(true);
    setStatus('');
    try {
      const imageContext = activeImageAttachment && imageAnalysis
        ? imageKnowledgeContextFor(activeImageAttachment.kind)
        : [];
      const input = {
        question: effectiveQuestion,
        chart: result,
        knowledgeContext: [...knowledgeContextFor(moduleId), ...imageContext],
        previousTurn: hasPreviousAnswerForRequest
          ? { question: previousQuestion, answer: previousAnswer }
          : undefined,
        additionalContext: imageAnalysis ? [`本机图像结构摘要：${imageAnalysis.promptText}`] : undefined
      };
      const onToken = (token: string) => {
        if (controllerRef.current !== controller) return;
        answerBufferRef.current = `${answerBufferRef.current}${token}`.slice(0, MAX_VISIBLE_ANSWER);
      };
      let generated = '';
      if (effectivePreset === 'rules') {
        setStatus('正在用本地规则整理盘面，不下载模型。');
        generated = buildRuleInterpretation({
          chart: result,
          question: effectiveQuestion,
          previousTurn: hasPreviousAnswerForRequest
            ? { question: previousQuestion, answer: previousAnswer }
            : undefined,
          additionalContext: imageAnalysis ? [`本机图像结构摘要：${imageAnalysis.promptText}`] : undefined
        });
        onToken(generated);
      } else if (isBrowserModelPreset(effectivePreset)) {
        generated = await streamBrowserInterpretation(input, { signal: controller.signal, onToken, onStatus: setStatus, modelId: effectiveModel as import('../ai/browserModel').BrowserModelId });
      } else {
        generated = await streamInterpretation({ baseUrl, apiKey, model: effectiveModel }, input, { signal: controller.signal, onToken });
      }
      const isBrowserModel = isBrowserModelPreset(effectivePreset);
      let fallback = '';
      if (isBrowserModel) {
        fallback = hasPreviousAnswerForRequest
          ? buildSafeFollowUpFallback(result, effectiveQuestion)
          : buildSafeInterpretationFallback(result, effectiveQuestion);
      }
      const acceptedAnswer = coerceInterpretation(generated, result, {
        requireChartEvidence: isBrowserModel,
        question: effectiveQuestion,
        previousAnswer,
        fallback
      });
      if (!acceptedAnswer.trim()) {
        throw new Error('模型没有给出可用回答，请重新生成。');
      }
      answerBufferRef.current = acceptedAnswer;
      answerCommittedRef.current = true;
      setAnswer(answerBufferRef.current);
      setConversationKey(resultKey);
      setConversation((current) => [
        ...(conversationKey === resultKey ? current : []),
        { question: effectiveQuestion, answer: answerBufferRef.current }
      ]);
      answeredQuestionRef.current = effectiveQuestion;
      setModelSettingsVisible(false);
    } catch (cause) {
      if (controllerRef.current !== controller) return;
      if (cause instanceof DOMException && cause.name === 'AbortError') {
        setError('已停止生成。');
      } else if (isBrowserModelPreset(effectivePreset)) {
        setError(formatBrowserModelError(cause));
      } else {
        setError(cause instanceof Error ? cause.message : 'AI 解读失败，请检查服务配置。');
      }
    } finally {
      if (controllerRef.current === controller) {
        setAnswer(answerCommittedRef.current ? answerBufferRef.current : '');
        controllerRef.current = null;
        requestContextRef.current = null;
        setLoading(false);
      }
    }
  }

  function clearConversation() {
    controllerRef.current?.abort();
    setConversationKey(resultKey);
    setConversation([]);
    setAnswer('');
    setAnswerResult(null);
    setFollowUpQuestion('');
    answeredQuestionRef.current = '';
    setError('');
    setStatus('');
    setModelSettingsVisible(true);
  }

  function startNewInterpretation() {
    clearConversation();
    setQuestionResult(result);
    setQuestion(result?.question || DEFAULT_QUESTION);
  }

  return (
    <aside className="ai-panel" aria-labelledby="ai-panel-title">
      <div className="ai-heading">
        <span className="ai-icon"><Icon name="spark" /></span>
        <div>
          <span className="eyebrow">主要阅读区 · 直接问答</span>
          <h2 id="ai-panel-title">解读工作区</h2>
        </div>
        <span className="ai-heading-badge">可连续追问</span>
      </div>
      <p className="ai-intro">选好模型后直接提问。回答会直接说结论，并用大白话解释盘面依据。</p>
      <p className="ai-contract-note"><Icon name="check" size={15} />回答完成后可以接着追问，不用重新排盘。</p>
      <p className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">{currentLoading ? 'AI 解读生成中。' : visibleAnswer ? 'AI 解读已完成。' : ''}</p>
      <div className="ai-flow">
        {(!hasPreviousAnswer || modelSettingsVisible) && <section className="ai-flow-step ai-model-step" aria-labelledby="ai-model-step-title">
          <div className="ai-step-heading">
            <span className="ai-step-number">01</span>
            <div>
              <strong id="ai-model-step-title">选择模型</strong>
              <span>先确认模型和处理位置</span>
            </div>
          </div>
          <label className="field">
            <span>模型服务</span>
            <select value={preset} onChange={(event) => handlePreset(event.target.value as ProviderPreset)} disabled={currentLoading}>
              <option value="auto">自动适配（推荐）</option>
              <option value="rules">免费规则解读（零下载、零 API）</option>
              <option value="browser">轻量本地模型（约 350 MB）</option>
              <option value="browserQuality">优质本地模型（约 1 GB）</option>
              <option value="deepseek">DeepSeek</option>
              <option value="openai">OpenAI</option>
              <option value="local">本机 Ollama（兼容接口）</option>
              <option value="custom">自定义兼容接口</option>
            </select>
          </label>
          {isOfflinePreset(preset) ? (
            <InterpretationModeCard preset={preset} environment={environment} />
          ) : (
            <details className="ai-settings" open={settingsOpen} onToggle={(event) => setSettingsOpen(event.currentTarget.open)}>
              <summary>模型连接设置 <span>地址、模型和密钥</span></summary>
              <label className="field">
                <span>API 地址 <em>必填</em></span>
                <input ref={baseUrlRef} type="url" value={baseUrl} onChange={(event) => handleBaseUrl(event.target.value)} spellCheck={false} disabled={currentLoading} required aria-required="true" aria-invalid={visibleErrorField === 'baseUrl'} aria-describedby={visibleErrorField === 'baseUrl' ? 'base-url-error' : undefined} />
                {visibleErrorField === 'baseUrl' && <small id="base-url-error" className="form-error" role="alert">{visibleError}</small>}
              </label>
              <label className="field">
                <span>模型名称 <em>必填</em></span>
                <input ref={modelRef} value={model} onChange={(event) => {
                  configTouchedRef.current = true;
                  setModel(event.target.value);
                  if (errorField === 'model') {
                    setError('');
                    setErrorField(null);
                  }
                }} spellCheck={false} disabled={currentLoading} required aria-required="true" aria-invalid={visibleErrorField === 'model'} aria-describedby={visibleErrorField === 'model' ? 'model-error' : undefined} />
                {visibleErrorField === 'model' && <small id="model-error" className="form-error" role="alert">{visibleError}</small>}
              </label>
              <label className="field">
                <span>API Key <em>{apiKeyRequired ? '必填' : '选填'}</em></span>
                <input ref={apiKeyRef} aria-label="API Key" aria-describedby={`api-key-note${visibleErrorField === 'apiKey' ? ' api-key-error' : ''}`} type="password" autoComplete="off" value={apiKey} onChange={(event) => handleApiKey(event.target.value)} placeholder={preset === 'local' ? '本机无鉴权时可留空' : '填写后可在此设备加密保存'} spellCheck={false} disabled={currentLoading} required={apiKeyRequired} aria-required={apiKeyRequired} aria-invalid={visibleErrorField === 'apiKey'} />
                <small id="api-key-note"><Icon name="key" size={14} />不写入 localStorage、Cookie、URL 或本站服务器。</small>
                {visibleErrorField === 'apiKey' && <small id="api-key-error" className="form-error" role="alert">{visibleError}</small>}
              </label>
              <label className="device-save-option">
                <input aria-label="在此浏览器设备记住这套设置" type="checkbox" checked={rememberOnDevice} onChange={(event) => handleRememberOnDevice(event.target.checked)} disabled={currentLoading} />
                <span><strong>在此浏览器设备记住这套设置</strong><small>API Key 会加密保存在本机浏览器，不上传本站；公共电脑请关闭。</small></span>
              </label>
              {deviceConfigError && <p className="form-error device-config-error" role="alert">{deviceConfigError}</p>}
            </details>
          )}
        </section>}

        {!hasPreviousAnswer && <section className="ai-flow-step ai-question-step" aria-labelledby="ai-question-step-title">
          <div className="ai-step-heading">
            <span className="ai-step-number">02</span>
            <div>
              <strong id="ai-question-step-title">提出问题并解读</strong>
              <span>告诉模型你最想先看什么</span>
            </div>
          </div>
          <label className="field">
            <span>你最想看什么？ <em>{hasPreviousAnswer ? '可继续追问' : '可选'}</em></span>
            <textarea aria-describedby="ai-question-note" rows={3} maxLength={300} value={visibleQuestion} onChange={(event) => {
              setQuestionResult(result);
              setQuestion(event.target.value);
            }} disabled={currentLoading} />
            <small id="ai-question-note">{hasPreviousAnswer ? '这次提问会沿用上一轮结论和盘面重点。' : '不确定怎么写？点下面的快捷问题。'} 请勿填写姓名、电话、身份证号、详细住址等敏感信息。</small>
          </label>
          <div className="question-presets" role="group" aria-label="快捷解读问题">
            {QUESTION_PRESETS.map((item) => (
              <button key={item.label} type="button" className="question-chip" disabled={currentLoading} onClick={() => {
                setQuestionResult(result);
                setQuestion(item.value);
              }}>{item.label}</button>
            ))}
          </div>

          <details className="optional-reading" open={imagePanelOpen} onToggle={(event) => setImagePanelOpen(event.currentTarget.open)}>
            <summary>可选：加入手相或面相图片研究 <span>原图不上传</span></summary>
            <ImageReadingInput value={activeImageAttachment} onChange={setImageAttachment} onAnalysis={setImageAnalysis} disabled={currentLoading} browserModelSelected={isOfflinePreset(effectivePreset)} />
          </details>

          <div className="destination-note"><Icon name="shield" size={18} /><span>{effectivePreset === 'rules' ? <>实际处理位置：<strong>当前网页内置规则，不下载模型，不上传盘面或原图</strong></> : isBrowserModelPreset(effectivePreset) ? <>实际处理位置：<strong>当前浏览器本机，不上传盘面或原图</strong></> : <>实际发送目标：<strong>{baseUrl ? fullDestination(baseUrl) : '尚未填写'}</strong>{activeImageAttachment && <small>；图片研究需切换回本地模式</small>}</>}</span></div>
          <details className="send-preview">
            <summary>查看将发送的内容</summary>
            <ul>
              <li>排盘系统、精确日期时间与结构化盘面</li>
              <li>摘要、分区事实、规则出处、警告</li>
              <li>当前术数的本地知识摘录</li>
              <li>你填写的关注点或快捷问题</li>
              {hasPreviousAnswer && <li>上一轮解读的结论和最多 3 条重点（仅用于关联追问）</li>}
              {activeImageAttachment && imageAnalysis && <li>{activeImageAttachment.kind === 'palm' ? '手相' : '面相'}图片：本机结构摘要（原图不发送）</li>}
            </ul>
          </details>
          {visibleError && !visibleErrorField && <p className="form-error" role="alert">{visibleError}</p>}
          <div className="ai-actions">
            {currentLoading ? (
              <button className="secondary-button danger-button" type="button" onClick={() => controllerRef.current?.abort()}>停止生成</button>
            ) : (
              <button className="secondary-button" type="button" aria-label="开始解读" onClick={() => void interpret()} disabled={!result}><Icon name="spark" size={18} />开始解读</button>
            )}
            {(apiKey || hasSavedConfig || clearingDeviceConfig) && <button className="text-button" type="button" aria-label="清除密钥" disabled={clearingDeviceConfig} onClick={() => void clearDeviceCredentials()}>{clearingDeviceConfig ? '正在清除…' : '清除此设备保存'}</button>}
          </div>
        </section>}

        {hasPreviousAnswer && (
          <div className="ai-session-toolbar">
            <span>当前解读方式：{PROVIDER_LABELS[preset]}</span>
            <button type="button" onClick={() => setModelSettingsVisible((visible) => !visible)} disabled={currentLoading}>
              {modelSettingsVisible ? '收起模型设置' : '更换模型'}
            </button>
          </div>
        )}

        {hasPreviousAnswer && visibleError && !visibleErrorField && (
          <p className="form-error ai-conversation-error" role="alert">{visibleError}</p>
        )}

        {(visibleConversation.length > 0 || currentLoading) && (
            <AiConversation
              turns={visibleConversation}
              loading={currentLoading}
              loadingQuestion={visibleQuestion}
              status={status}
              followUpQuestion={followUpQuestion}
              onFollowUpQuestionChange={(value) => {
                setFollowUpQuestion(value);
                setQuestionResult(result);
                setQuestion(value);
              }}
              onFollowUp={() => void interpret(followUpQuestion)}
              onClear={clearConversation}
              onStartNew={startNewInterpretation}
            />
        )}
      </div>

      <div className="ai-disclaimer">
        <strong>辅助解读内容</strong>
        <span>依据用户输入、程序排盘和所选解读方式生成</span>
        <p>可能存在错误或遗漏，仅供传统文化研究与娱乐，不构成事实、预测或现实决策依据。</p>
      </div>
    </aside>
  );
}
